import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const ENDPOINT = "/admin/wp-offers/reprice-unsent";
const DEFAULT_INTRO = "Aktuelle Paket- und Katalogpreise + Provision des zuständigen Verkäufers. Versendete oder unterschriebene Angebote bleiben unverändert, freie Zeilen behalten ihren Preis. Angebote mit bewusst von Hand gesetzten Preisen hier abwählen.";

function OfferRow({ o, checked, onToggle, locked }) {
  return (
    <div className={`rounded-xl border bg-[#0F0F11] p-3 ${checked ? "border-zinc-700" : "border-zinc-800 opacity-60"}`} data-testid={`reprice-row-${o.offer_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="min-w-0 flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={checked} disabled={locked} onChange={onToggle}
            data-testid={`reprice-row-check-${o.offer_id}`} className="mt-1 accent-[#00E575]" />
          <div className="min-w-0">
            <div className="text-[13px] font-bold truncate">{o.offer_number} · {o.customer_name || "Kunde"}</div>
            <div className="text-[11px] text-zinc-500 truncate">{o.project_number} · {o.package_name}</div>
          </div>
        </label>
        <div className="text-[12.5px] font-mono text-right">
          <span className="text-zinc-500 line-through">{fmt(o.old_net)}</span> → <b className="text-[#00E575]">{fmt(o.new_net)}</b>
          <div className="text-[10.5px] text-zinc-500">netto vor Rabatt</div>
        </div>
      </div>
      <ul className="mt-2 space-y-0.5 text-[11.5px] text-zinc-400">
        {o.changes.map((c, i) => (
          <li key={i} className="truncate">{c.title}: <span className="line-through text-zinc-600">{fmt(c.old)}</span> → {fmt(c.new)}</li>
        ))}
      </ul>
      {o.applied === true && <div className="mt-1.5 text-[11.5px] text-[#00E575] font-semibold">Neu berechnet · PDF neu erstellt</div>}
      {o.applied === false && <div className="mt-1.5 text-[11.5px] text-red-400 font-semibold">Fehler: {o.error}</div>}
    </div>
  );
}

// Vorschau + Neuberechnung nicht versendeter WP-Angebote (optional gefiltert auf Paket/Komponente).
export function RepriceUnsentDialog({ open, onOpenChange, filter, preset, title, intro }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(() => new Set());
  const toggle = (id) => setSkip((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    if (!open) return;
    setSkip(new Set());
    if (preset) { setData(preset); return; }
    setData(null);
    setBusy(true);
    api.post(ENDPOINT, { dry_run: true, ...(filter || {}) })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen"))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Einzeln nacheinander – vermeidet Timeouts (je Angebot wird das PDF neu erstellt).
  const applyAll = async () => {
    const todo = data.offers.filter((o) => !skip.has(o.offer_id));
    if (!todo.length) { toast.info("Kein Angebot ausgewählt"); return; }
    if (!window.confirm(`${todo.length} Angebot(e) jetzt neu berechnen?\n\nPreise und PDFs werden überschrieben – auch von Hand geänderte Preise bei Katalog-Positionen. Versendete Angebote bleiben unverändert.`)) return;
    setBusy(true);
    let ok = 0;
    for (const o of todo) {
      let patch;
      try {
        const { data: res } = await api.post(ENDPOINT, { dry_run: false, offer_ids: [o.offer_id] });
        patch = res.offers[0] || { applied: true };
        if (patch.applied) ok += 1;
      } catch (e) {
        patch = { applied: false, error: e.response?.data?.detail || "Fehler" };
      }
      setData((d) => ({ ...d, offers: d.offers.map((x) => (x.offer_id === o.offer_id ? { ...x, ...patch } : x)) }));
    }
    setData((d) => ({ ...d, dry_run: false }));
    setBusy(false);
    toast.success(`${ok} Angebot(e) neu berechnet`);
  };
  const applied = data && !data.dry_run;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#18181B] border-zinc-800 text-zinc-100 max-h-[90vh] flex flex-col" data-testid="wp-reprice-dialog">
        <DialogTitle className="text-[16px] font-bold">{title || "Nicht versendete Angebote neu berechnen"}</DialogTitle>
        <DialogDescription className="text-[12px] text-zinc-400 -mt-2">{intro || DEFAULT_INTRO}</DialogDescription>
        <div className="flex-1 overflow-auto space-y-2 pr-1" data-testid="wp-reprice-list">
          {busy && !data && <div className="py-8 text-center text-zinc-400 text-sm"><Loader2 className="animate-spin inline mr-2" size={14} />Prüfe Angebote…</div>}
          {data && data.count === 0 && <div className="py-8 text-center text-zinc-400 text-sm" data-testid="wp-reprice-empty">Alle nicht versendeten Angebote sind bereits auf dem aktuellen Stand.</div>}
          {data?.offers.map((o) => (
            <OfferRow key={o.offer_id} o={o} checked={!skip.has(o.offer_id)} onToggle={() => toggle(o.offer_id)} locked={busy || applied} />
          ))}
        </div>
        {data && data.count > 0 && !applied && (
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} disabled={busy} data-testid="wp-reprice-later"
              className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-200 text-[13px] font-semibold hover:bg-zinc-700">
              Später
            </button>
            <button onClick={applyAll} disabled={busy} data-testid="wp-reprice-apply"
              className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:opacity-60">
              {busy && <Loader2 className="animate-spin" size={14} />} {data.count - skip.size} Angebot(e) jetzt neu berechnen
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RepriceUnsentButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="wp-pkgs-reprice-open"
        className="h-9 px-3 rounded-lg bg-[#18181B] border border-amber-500/40 hover:border-amber-400 text-amber-200 text-[12px] inline-flex items-center gap-1.5 font-medium">
        <Calculator size={13} /> Angebote neu berechnen
      </button>
      <RepriceUnsentDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

// Nach dem Speichern von Paket/Komponente: betroffene, nicht versendete Angebote anzeigen.
export function useRepriceAfterSave() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const allowed = ["admin", "sales_director", "super_admin"].includes(user?.role) || user?.is_super_admin;
  const check = async (filter, label) => {
    if (!allowed) return;
    try {
      const { data } = await api.post(ENDPOINT, { dry_run: true, ...filter });
      if (data.count > 0) setState({ filter, data, label });
    } catch { /* Hinweis ist optional */ }
  };
  const dialog = (
    <RepriceUnsentDialog
      open={!!state}
      onOpenChange={(o) => !o && setState(null)}
      filter={state?.filter}
      preset={state?.data}
      title={state ? `${state.data.count} nicht versendete${state.data.count === 1 ? "s Angebot" : " Angebote"} betroffen` : ""}
      intro={state ? `Die Preisänderung an „${state.label}“ betrifft diese Angebote. Jetzt mit aktuellen Preisen + Provision des zuständigen Verkäufers neu berechnen? Versendete Angebote bleiben unverändert.` : ""}
    />
  );
  return { check, dialog };
}
